import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import AuthService from '../services/auth.service';

interface UseUnsavedChangesWarningProps {
  when: boolean; // Enable blocking when true
  onConfirm?: () => void; // Optional callback when user confirms navigation
  onCancel?: () => void; // Optional callback when user cancels navigation
}

interface UseUnsavedChangesWarningReturn {
  showPrompt: boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
}

/**
 * Custom hook to warn users about unsaved changes before navigating away
 * @param when - Boolean to enable/disable the warning
 * @param onConfirm - Optional callback when user confirms navigation
 * @param onCancel - Optional callback when user cancels navigation
 * @returns Object with showPrompt state and confirm/cancel handlers
 */
const useUnsavedChangesWarning = ({
  when,
  onConfirm,
  onCancel
}: UseUnsavedChangesWarningProps): UseUnsavedChangesWarningReturn => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  // Check feature flag in localStorage via AuthService
  const isFeatureEnabled = AuthService.getConfirmationFormRouting() === 'true';
  const shouldBlock = when && isFeatureEnabled;

  // Block navigation when 'when' is true and feature is enabled
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldBlock && !confirmedNavigation && currentLocation.pathname !== nextLocation.pathname
  );

  // Show prompt when blocker is triggered
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowPrompt(true);
    }
  }, [blocker.state]);

  // Handle beforeunload event for page refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlock) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  const confirmNavigation = useCallback(() => {
    setShowPrompt(false);
    setConfirmedNavigation(true);
    onConfirm?.();

    // Proceed with blocked navigation
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker, onConfirm]);

  const cancelNavigation = useCallback(() => {
    setShowPrompt(false);
    onCancel?.();

    // Reset blocker
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker, onCancel]);

  return {
    showPrompt,
    confirmNavigation,
    cancelNavigation
  };
};

export default useUnsavedChangesWarning;
